import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import {
  SurveyQuestionCategoryData,
  getSurveyQuestionCategories,
  saveSurveyQuestionCategory,
  updateSurveyQuestionCategory,
  deleteSurveyQuestionCategory,
  checkSurveyQuestionCategoryKeyExists,
  checkSurveyQuestionCategoryNameExists,
  getSurveyQuestionsByCategory,
  getSurveyQuestions,
} from '@/lib/firebase';
import { useAuth } from '@/contexts/auth-context';
import { Tag, Plus, Edit, Trash2, ChevronUp, ChevronDown, GripVertical } from 'lucide-react';
import { Link } from 'wouter';

const formatModified = (ts: any): string => {
  if (!ts) return '-';
  try {
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '-';
  }
};

interface FormData {
  key: string;
  name: string;
  description: string;
}

const EMPTY_FORM: FormData = { key: '', name: '', description: '' };

export default function PulseSurveyCategoriesPage() {
  const [categories, setCategories] = useState<SurveyQuestionCategoryData[]>([]);
  const [questionCounts, setQuestionCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<SurveyQuestionCategoryData | null>(null);
  const [formData, setFormData] = useState<FormData>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [keyError, setKeyError] = useState('');
  const [nameError, setNameError] = useState('');

  // Reorder mode state
  const [reorderMode, setReorderMode] = useState(false);
  const [reorderList, setReorderList] = useState<SurveyQuestionCategoryData[]>([]);
  const [savingOrder, setSavingOrder] = useState(false);

  const { user } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    if (user) loadCategories();
  }, [user]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && user) {
        loadCategories();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const [data, questions] = await Promise.all([
        getSurveyQuestionCategories(),
        getSurveyQuestions(),
      ]);
      const sorted = data.sort((a, b) => a.displayOrder - b.displayOrder);
      setCategories(sorted);
      const counts: Record<string, number> = {};
      for (const q of questions) {
        if (q.categoryId) {
          counts[q.categoryId] = (counts[q.categoryId] ?? 0) + 1;
        }
      }
      setQuestionCounts(counts);
    } catch (error: any) {
      toast({ title: 'Failed to load categories', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditing(null);
    setFormData(EMPTY_FORM);
    setKeyError('');
    setNameError('');
    setModalOpen(true);
  };

  const openEdit = (cat: SurveyQuestionCategoryData) => {
    setEditing(cat);
    setFormData({ key: cat.key, name: cat.name, description: cat.description });
    setKeyError('');
    setNameError('');
    setModalOpen(true);
  };

  const validateForm = async (): Promise<boolean> => {
    let valid = true;

    if (!formData.key.trim()) {
      setKeyError('Key is required');
      valid = false;
    } else if (!editing) {
      const exists = await checkSurveyQuestionCategoryKeyExists(formData.key.trim());
      if (exists) {
        setKeyError('This key already exists');
        valid = false;
      } else {
        setKeyError('');
      }
    } else {
      setKeyError('');
    }

    if (!formData.name.trim()) {
      setNameError('Name is required');
      valid = false;
    } else {
      const nameExists = await checkSurveyQuestionCategoryNameExists(
        formData.name.trim(),
        editing ? editing.key : undefined
      );
      if (nameExists) {
        setNameError('This name already exists');
        valid = false;
      } else {
        setNameError('');
      }
    }

    if (!formData.description.trim()) {
      toast({ title: 'Description required', description: 'Please enter a description.', variant: 'destructive' });
      valid = false;
    }

    return valid;
  };

  const handleSave = async () => {
    const valid = await validateForm();
    if (!valid) return;

    try {
      setSaving(true);
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        displayOrder: editing ? editing.displayOrder : categories.length,
      };

      if (editing) {
        await updateSurveyQuestionCategory(editing.key, payload);
        toast({ title: 'Category updated', description: `${payload.name} has been updated.` });
      } else {
        await saveSurveyQuestionCategory(formData.key.trim(), payload);
        toast({ title: 'Category created', description: `${payload.name} has been added.` });
      }
      setModalOpen(false);
      loadCategories();
    } catch (error: any) {
      toast({ title: 'Failed to save', description: error.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (cat: SurveyQuestionCategoryData) => {
    try {
      const assignedQuestions = await getSurveyQuestionsByCategory(cat.key);
      if (assignedQuestions.length > 0) {
        toast({
          title: 'Cannot delete category',
          description: `This category has ${assignedQuestions.length} question${assignedQuestions.length === 1 ? '' : 's'} assigned to it. Reassign or delete those questions first.`,
          variant: 'destructive',
        });
        return;
      }
      if (!confirm(`Delete category "${cat.name}"? This cannot be undone.`)) return;
      await deleteSurveyQuestionCategory(cat.key);
      toast({ title: 'Category deleted', description: `${cat.name} has been deleted.` });
      loadCategories();
    } catch (error: any) {
      toast({ title: 'Failed to delete', description: error.message, variant: 'destructive' });
    }
  };

  // --- Reorder mode ---

  const enterReorderMode = () => {
    setReorderList([...categories]);
    setReorderMode(true);
  };

  const cancelReorder = () => {
    setReorderMode(false);
    setReorderList([]);
  };

  const moveItem = (index: number, direction: 'up' | 'down') => {
    const list = [...reorderList];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= list.length) return;
    [list[index], list[swapIndex]] = [list[swapIndex], list[index]];
    setReorderList(list);
  };

  const saveOrder = async () => {
    try {
      setSavingOrder(true);
      await Promise.all(
        reorderList.map((cat, i) =>
          updateSurveyQuestionCategory(cat.key, { displayOrder: i })
        )
      );
      toast({ title: 'Order saved', description: 'Category order has been updated.' });
      setReorderMode(false);
      loadCategories();
    } catch (error: any) {
      toast({ title: 'Failed to save order', description: error.message, variant: 'destructive' });
    } finally {
      setSavingOrder(false);
    }
  };

  const displayList = reorderMode ? reorderList : categories;

  return (
    <div className="flex-1 p-8">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-3">
          <Tag className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Survey Question Categories</h1>
            <p className="text-slate-600">Manage categories for pulse survey questions</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {reorderMode ? (
            <>
              <Button variant="outline" onClick={cancelReorder} disabled={savingOrder}>
                Cancel
              </Button>
              <Button onClick={saveOrder} disabled={savingOrder}>
                {savingOrder ? 'Saving...' : 'Save Order'}
              </Button>
            </>
          ) : (
            <>
              {categories.length > 1 && (
                <Button variant="outline" onClick={enterReorderMode} className="flex items-center gap-2">
                  <GripVertical className="w-4 h-4" />
                  Reorder
                </Button>
              )}
              <Button onClick={openAdd} className="flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Add Category
              </Button>
            </>
          )}
        </div>
      </div>

      {reorderMode && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-md px-4 py-2 text-sm text-amber-800">
          Use the arrows to reorder categories, then click <strong>Save Order</strong> to apply.
        </div>
      )}

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-slate-600">Loading categories...</p>
        </div>
      ) : categories.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Tag className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600 mb-4">No categories yet</p>
            <Button onClick={openAdd}>
              <Plus className="w-4 h-4 mr-2" />
              Add First Category
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    {reorderMode && <th className="px-4 py-3 w-24" />}
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Questions</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Modified</th>
                    {!reorderMode && (
                      <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {displayList.map((cat, index) => (
                    <tr key={cat.key} className={reorderMode ? 'bg-white' : 'hover:bg-slate-50'}>
                      {reorderMode && (
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              disabled={index === 0}
                              onClick={() => moveItem(index, 'up')}
                            >
                              <ChevronUp className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              disabled={index === displayList.length - 1}
                              onClick={() => moveItem(index, 'down')}
                            >
                              <ChevronDown className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      )}
                      <td className="px-6 py-4">
                        <div className="font-medium text-slate-900">{cat.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {(() => {
                          const count = questionCounts[cat.key] ?? 0;
                          return count === 0 ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-400">
                              0
                            </span>
                          ) : (
                            <Link href={`/pulse-surveys/questions?category=${encodeURIComponent(cat.key)}`}>
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 cursor-pointer transition-colors">
                                {count}
                              </span>
                            </Link>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">{formatModified(cat.modified)}</td>
                      {!reorderMode && (
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(cat)} className="mr-2">
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(cat)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Category' : 'Add Category'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label htmlFor="cat-key">Key * (used as document ID)</Label>
              <Input
                id="cat-key"
                value={formData.key}
                onChange={(e) => { setFormData(prev => ({ ...prev, key: e.target.value })); setKeyError(''); }}
                placeholder="e.g., mental-health"
                disabled={!!editing}
                className={keyError ? 'border-red-500' : ''}
              />
              {keyError && <p className="text-sm text-red-600 mt-1">{keyError}</p>}
              {!editing && (
                <p className="text-xs text-slate-500 mt-1">Unique identifier — cannot be changed after creation</p>
              )}
            </div>

            <div>
              <Label htmlFor="cat-name">Name *</Label>
              <Input
                id="cat-name"
                value={formData.name}
                onChange={(e) => { setFormData(prev => ({ ...prev, name: e.target.value })); setNameError(''); }}
                placeholder="Enter category name"
                className={nameError ? 'border-red-500' : ''}
              />
              {nameError && <p className="text-sm text-red-600 mt-1">{nameError}</p>}
            </div>

            <div>
              <Label htmlFor="cat-description">Description *</Label>
              <Textarea
                id="cat-description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Enter description"
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : editing ? 'Save Changes' : 'Add Category'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
